
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export default function SlackCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, session } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState<"processing" | "success" | "error" | "waiting_auth">("waiting_auth");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const apiCallMadeRef = useRef(false);
  const authCheckCompletedRef = useRef(false);

  // Function to handle the actual Slack OAuth flow
  const handleSlackCallback = async (userSession) => {
    // Prevent multiple API calls
    if (apiCallMadeRef.current) return;
    apiCallMadeRef.current = true;
    
    try {
      // Parse URL parameters
      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      const error = params.get("error");
      const state = params.get("state");
      
      console.log("=== SLACK CALLBACK DEBUG ===");
      console.log("Full callback URL:", window.location.href);
      console.log("Search params:", location.search);
      console.log("Code present:", !!code);
      console.log("Error param:", error);
      console.log("State param:", state);
      
      if (error) {
        console.error("OAuth error from Slack:", error);
        throw new Error(`Authorization denied: ${error}`);
      }
      
      if (!code) {
        console.error("No authorization code received from Slack");
        throw new Error("No authorization code received");
      }
      
      if (!userSession || !userSession.user) {
        throw new Error("You must be logged in to connect a Slack workspace");
      }

      console.log(`Found session, proceeding with OAuth flow`);
      
      // Parse the state parameter
      let userId = userSession.user.id;
      let parsedWorkspaceName = "Workspace";
      
      try {
        if (state) {
          // Check if state is a JSON string
          if (state.startsWith('{') && state.endsWith('}')) {
            const stateData = JSON.parse(state);
            // Make sure the userId matches for security
            if (stateData.userId === userSession.user.id) {
              userId = stateData.userId;
              parsedWorkspaceName = stateData.workspaceName || "Workspace";
              setWorkspaceName(parsedWorkspaceName);
            }
          } else {
            // Legacy format: just the userId
            if (state === userSession.user.id) {
              userId = state;
            }
          }
        }
      } catch (e) {
        console.error("Failed to parse state parameter:", e);
        // Continue with current user ID if parsing fails
      }
      
      // Use the exact same redirect URI that was used for the OAuth request
      const redirectUri = `https://sharedmatter.com/slack/callback`;
      console.log("Using EXACT redirect URI for token exchange:", redirectUri);
      
      // Call our Edge Function to complete the OAuth flow
      console.log("Making Slack OAuth API call with code:", code.substring(0, 5) + "...", "User ID:", userId);
      
      const response = await fetch("https://erticlijceutpetbahff.supabase.co/functions/v1/slack-oauth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${userSession.access_token}`,
        },
        body: JSON.stringify({
          code,
          userId,
          workspaceName: parsedWorkspaceName,
          redirectUri: redirectUri
        }),
      });
      
      const responseStatus = response.status;
      let data;
      
      try {
        data = await response.json();
      } catch (e) {
        console.error("Failed to parse response JSON:", e);
        throw new Error(`Failed to parse response (status ${responseStatus})`);
      }
      
      console.log("Slack OAuth response status:", responseStatus);
      console.log("Slack OAuth response data:", data);
      
      if (!response.ok) {
        console.error("Slack OAuth API error:", data.error);
        throw new Error(data.error || `Failed to connect Slack workspace (status ${responseStatus})`);
      }
      
      setStatus("success");
      toast({
        title: "Slack workspace connected",
        description: `Successfully connected to ${data.team_name}`,
      });
      
      // Redirect after a brief delay
      setTimeout(() => navigate("/slack"), 1500);
    } catch (error) {
      console.error("Slack OAuth error:", error);
      setStatus("error");
      setErrorMessage(error.message);
      toast({
        variant: "destructive",
        title: "Connection failed",
        description: error.message,
      });
    }
  };

  // Effect to wait for authentication to be ready
  useEffect(() => {
    const checkAuthAndProceed = async () => {
      try {
        // Only proceed if we haven't already made the API call and haven't completed auth check
        if (apiCallMadeRef.current || authCheckCompletedRef.current) return;
        authCheckCompletedRef.current = true;
        
        // Get the latest session from Supabase directly
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Error checking session:", sessionError);
          setStatus("error");
          setErrorMessage("Error checking authentication status: " + sessionError.message);
          return;
        }
        
        if (sessionData?.session && sessionData.session.user) {
          console.log("User authenticated, proceeding with OAuth flow");
          setStatus("processing");
          handleSlackCallback(sessionData.session);
        } else {
          console.log("User not authenticated, showing login prompt");
          setStatus("waiting_auth");
          setErrorMessage("You must be logged in to connect a Slack workspace");
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        setStatus("error");
        setErrorMessage("Error verifying authentication status: " + error.message);
      }
    };
    
    // Add a small delay to ensure any auth state changes have propagated
    const timer = setTimeout(() => {
      checkAuthAndProceed();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [location.search]); // Only run on location.search change

  // Also run the check when session changes (login/logout)
  useEffect(() => {
    if (session && currentUser && status === "waiting_auth") {
      console.log("Session detected, proceeding with OAuth flow");
      setStatus("processing");
      handleSlackCallback(session);
    }
  }, [session, currentUser, status]);

  const loginAndRetry = () => {
    // Store the current URL in sessionStorage to redirect back here after login
    sessionStorage.setItem('slackCallbackUrl', window.location.href);
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1A1F2C] via-[#221F26] to-[#1A1F2C]">
      <div className="bg-white/80 backdrop-blur-md p-8 rounded-lg shadow-xl text-center max-w-md w-full">
        <h1 className="text-2xl font-bold mb-4">
          {status === "processing" && `Connecting ${workspaceName || "Slack"}...`}
          {status === "waiting_auth" && "Authentication Required"}
          {status === "success" && `${workspaceName || "Slack"} Connected!`}
          {status === "error" && "Connection Failed"}
        </h1>
        
        <p className="text-gray-600 mb-6">
          {status === "processing" && "Please wait while we connect your Slack workspace."}
          {status === "waiting_auth" && "You need to be logged in to connect a Slack workspace."}
          {status === "success" && "Your Slack workspace has been successfully connected."}
          {status === "error" && errorMessage}
        </p>
        
        {(status === "processing") && (
          <div className="flex justify-center">
            <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
          </div>
        )}
        
        {status === "success" && (
          <Button
            onClick={() => navigate("/slack")}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
          >
            Go to Slack Settings
          </Button>
        )}
        
        {(status === "waiting_auth" || (status === "error" && errorMessage?.includes("logged in"))) && (
          <div className="space-y-4">
            <Button
              onClick={loginAndRetry}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
            >
              Log In
            </Button>
            <p className="text-sm text-gray-500 mt-2">
              After logging in, you'll be redirected back to complete the Slack connection.
            </p>
          </div>
        )}
        
        {status === "error" && !errorMessage?.includes("logged in") && (
          <Button
            onClick={() => navigate("/slack")}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
          >
            Back to Slack Settings
          </Button>
        )}
      </div>
    </div>
  );
}
